"use client";

import { useEffect, useRef, useState } from "react";
import { valuePropSlides, type ValuePropSlide } from "@/lib/onboardingSlides";
import { useTranslations } from "@/contexts/I18nContext";

/**
 * Shared value-prop carousel used by the onboarding gate. Mirrors the iOS
 * `TabView(.tabViewStyle(.page))` + `ValuePropSlideView` pair in
 * `ContentView.swift`. No heavyweight carousel library — native CSS
 * scroll-snap with a tiny IntersectionObserver to track the active slide.
 *
 * Each slide is a full-frame cover image with a 3-stop dark-to-transparent
 * gradient scrim matching the iOS values (0.78 bottom → 0.45 mid → clear at
 * 70% up) so the tagline stays legible against bright outdoor compositions.
 */
export function ValuePropCarousel() {
  const t = useTranslations();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // Track which slide is currently centered in the viewport.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the most-visible slide as active.
        let best: IntersectionObserverEntry | null = null;
        for (const entry of entries) {
          if (!best || entry.intersectionRatio > best.intersectionRatio) {
            best = entry;
          }
        }
        if (best && best.isIntersecting) {
          const idx = Number((best.target as HTMLElement).dataset.slideIndex);
          if (!Number.isNaN(idx)) setActiveIndex(idx);
        }
      },
      {
        root: scroller,
        threshold: [0.5, 0.75, 1],
      }
    );

    const slides = scroller.querySelectorAll<HTMLElement>("[data-slide-index]");
    slides.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollToSlide = (idx: number) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    // Scroll the carousel's own horizontal scroller directly. Using
    // slide.scrollIntoView() here would also adjust the document scroll
    // position whenever the carousel sits off-screen — on mobile web with
    // the soft keyboard up, that yanks the focused auth input under the
    // keyboard every 3s on auto-advance.
    scroller.scrollTo({ left: idx * scroller.clientWidth, behavior: "smooth" });
  };

  // Auto-advance every 3s, wrapping from the last slide back to the first.
  // We read the current index from a ref (not the closure) so the interval
  // doesn't need to be recreated on every activeIndex change.
  const activeIndexRef = useRef(activeIndex);
  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    const id = window.setInterval(() => {
      const next = (activeIndexRef.current + 1) % valuePropSlides.length;
      scrollToSlide(next);
    }, 3000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="relative w-full h-full flex flex-col">
      <div
        ref={scrollerRef}
        className="flex-1 flex overflow-x-auto snap-x snap-mandatory scroll-smooth rounded-2xl shadow-md"
        style={{ scrollbarWidth: "none" }}
      >
        {/* Hide scrollbar on WebKit browsers */}
        <style>{`
          [data-slide-scroller]::-webkit-scrollbar { display: none; }
        `}</style>
        <div data-slide-scroller className="contents">
          {valuePropSlides.map((slide, idx) => (
            <Slide key={slide.id} slide={slide} index={idx} t={t} />
          ))}
        </div>
      </div>

      {/* Paging dots */}
      <div className="flex items-center justify-center gap-2 pt-3">
        {valuePropSlides.map((slide, idx) => {
          const active = idx === activeIndex;
          return (
            <button
              key={slide.id}
              type="button"
              aria-label={t("valueProp.dotAria", { n: idx + 1 })}
              onClick={() => scrollToSlide(idx)}
              className={`h-2 rounded-full transition-all ${
                active ? "w-6 bg-gray-800 dark:bg-white" : "w-2 bg-gray-400/60"
              }`}
            />
          );
        })}
      </div>
    </div>
  );
}

function Slide({ slide, index, t }: { slide: ValuePropSlide; index: number; t: (key: string, params?: Record<string, string | number>) => string }) {
  return (
    <div
      data-slide-index={index}
      className="relative flex-none w-full h-full snap-center snap-always overflow-hidden rounded-2xl"
    >
      <img
        src={slide.imageSrc}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: slide.objectPosition ?? "center" }}
        draggable={false}
      />

      {/* 3-stop gradient scrim matching iOS ValuePropSlideView. Bottom-up:
          0.78 black at the bottom, 0.45 at 35%, transparent at 70%. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.45) 35%, rgba(0,0,0,0) 70%)",
        }}
      />

      {/* Tagline. Text shadow is belt-and-suspenders contrast against bright
          skies / clouds that can leak through the scrim. */}
      <p
        className="absolute inset-x-0 bottom-0 px-6 pb-12 text-white text-center text-2xl sm:text-3xl font-semibold leading-snug"
        style={{ textShadow: "0 2px 4px rgba(0,0,0,0.6)" }}
      >
        {t(slide.taglineKey)}
      </p>
    </div>
  );
}
